// Packet strip (owned by the web:charts agent).
//
// One cell per packet, class-coloured, wrapping into rows. Colour is never the
// only signal: late/lost cells carry a glyph (hatch or mark) so the strip reads
// correctly in greyscale and for colour-vision deficiency. Rendered directly to
// a canvas in a single pass so it stays smooth at 100k+ packets. Hovering reads
// out the packet under the cursor; a summary line gives per-class totals.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { PacketClass, PacketRecord, RunResult } from '@/engine/types';
import { EmptyNote } from './Canvas';
import { PACKET_CLASS_LABEL, classColor, useChartTheme, type ChartPalette } from './palette';

export interface PacketStripProps {
  run: RunResult;
  /** Target cell edge in logical px (min 4). */
  cellSize?: number;
  /** Max visual height before the strip scrolls (px). */
  maxHeight?: number;
}

interface Layout {
  cols: number;
  rows: number;
  cell: number;
  gap: number;
  height: number;
}

export function PacketStrip({ run, cellSize = 7, maxHeight = 420 }: PacketStripProps) {
  const { palette } = useChartTheme();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<PacketRecord | null>(null);
  const layoutRef = useRef<Layout | null>(null);

  const packets = run.packets;
  const cell = Math.max(4, cellSize);
  const gap = cell >= 6 ? 1 : 0;

  const counts = useMemo(() => classCounts(packets), [packets]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const layout = useMemo<Layout>(() => {
    const step = cell + gap;
    const cols = Math.max(1, Math.floor((width || 1) / step));
    const rows = Math.max(1, Math.ceil(packets.length / cols));
    return { cols, rows, cell, gap, height: rows * step };
  }, [width, packets.length, cell, gap]);
  layoutRef.current = layout;

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || width <= 0) return;
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const h = layout.height;
    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(h * dpr));
    canvas.style.width = `${width}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, h);
    drawStrip(ctx, packets, layout, palette);
  }, [width, layout, packets, palette]);

  useEffect(() => {
    render();
  }, [render]);

  const onMove = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      const lo = layoutRef.current;
      if (!lo) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const step = lo.cell + lo.gap;
      const col = Math.floor((e.clientX - rect.left) / step);
      const row = Math.floor((e.clientY - rect.top) / step);
      const idx = row * lo.cols + col;
      setHover(idx >= 0 && idx < packets.length && col < lo.cols ? packets[idx] : null);
    },
    [packets],
  );

  if (packets.length === 0) return <EmptyNote label="No packets recorded." />;

  return (
    <figure style={{ margin: 0 }}>
      <div style={{ minHeight: 18, fontSize: 12, color: palette.textMuted, marginBottom: 4 }}>
        {hover
          ? `#${hover.seq} · ${PACKET_CLASS_LABEL[hover.cls]}${
              hover.rttMs != null ? ` · ${hover.rttMs.toFixed(2)} ms` : ''
            }${hover.outOfOrder ? ' · out of order' : ''}`
          : `${packets.length.toLocaleString('en-US')} packets`}
      </div>
      <div
        ref={wrapRef}
        style={{
          width: '100%',
          maxHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
          border: `1px solid ${palette.grid}`,
          borderRadius: 4,
          background: palette.panel,
        }}
      >
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`Packet strip: ${describeCounts(counts)}`}
          style={{ display: 'block' }}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        />
      </div>
      <ul
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 12px',
          listStyle: 'none',
          margin: '6px 0 0',
          padding: 0,
          fontSize: 12,
          color: palette.textMuted,
        }}
      >
        {(Object.keys(counts) as PacketClass[])
          .filter((c) => counts[c] > 0)
          .map((c) => (
            <li key={c} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <GlyphSwatch cls={c} palette={palette} />
              <span>
                {PACKET_CLASS_LABEL[c]}: {counts[c].toLocaleString('en-US')}
              </span>
            </li>
          ))}
      </ul>
    </figure>
  );
}

function GlyphSwatch({ cls, palette }: { cls: PacketClass; palette: ChartPalette }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = 14 * dpr;
    c.height = 14 * dpr;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawCell(ctx, 0, 0, 14, cls, palette);
  }, [cls, palette]);
  return <canvas ref={ref} aria-hidden style={{ width: 14, height: 14, borderRadius: 2 }} />;
}

// --- drawing --------------------------------------------------------------

function drawStrip(
  ctx: CanvasRenderingContext2D,
  packets: PacketRecord[],
  layout: Layout,
  palette: ChartPalette,
) {
  const step = layout.cell + layout.gap;
  const drawGlyphs = layout.cell >= 5;
  for (let i = 0; i < packets.length; i++) {
    const col = i % layout.cols;
    const row = Math.floor(i / layout.cols);
    const x = col * step;
    const y = row * step;
    drawCell(ctx, x, y, layout.cell, packets[i].cls, palette, drawGlyphs);
  }
}

/** Draw one class-coloured cell with a distinguishing glyph. */
function drawCell(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  cls: PacketClass,
  palette: ChartPalette,
  withGlyph = true,
) {
  ctx.fillStyle = classColor(palette, cls);
  ctx.fillRect(x, y, size, size);
  if (!withGlyph) return;
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = Math.max(1, size / 7);
  const pad = size * 0.22;
  ctx.beginPath();
  switch (cls) {
    case 'late': // top notch bar
      ctx.moveTo(x + pad, y + pad);
      ctx.lineTo(x + size - pad, y + pad);
      break;
    case 'lostUp': // forward slash
      ctx.moveTo(x + pad, y + size - pad);
      ctx.lineTo(x + size - pad, y + pad);
      break;
    case 'lostDown': // back slash
      ctx.moveTo(x + pad, y + pad);
      ctx.lineTo(x + size - pad, y + size - pad);
      break;
    case 'unknown': // cross
      ctx.moveTo(x + pad, y + pad);
      ctx.lineTo(x + size - pad, y + size - pad);
      ctx.moveTo(x + size - pad, y + pad);
      ctx.lineTo(x + pad, y + size - pad);
      break;
    case 'notSent': // hollow (draw dot)
      ctx.restore();
      ctx.save();
      ctx.fillStyle = palette.textMuted;
      ctx.beginPath();
      ctx.arc(x + size / 2, y + size / 2, Math.max(0.5, size * 0.12), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    case 'ok':
    default:
      ctx.restore();
      return;
  }
  ctx.stroke();
  ctx.restore();
}

function classCounts(packets: PacketRecord[]): Record<PacketClass, number> {
  const c: Record<PacketClass, number> = {
    ok: 0,
    late: 0,
    lostUp: 0,
    lostDown: 0,
    unknown: 0,
    notSent: 0,
  };
  for (const p of packets) c[p.cls] += 1;
  return c;
}

function describeCounts(counts: Record<PacketClass, number>): string {
  return (Object.keys(counts) as PacketClass[])
    .filter((k) => counts[k] > 0)
    .map((k) => `${counts[k]} ${PACKET_CLASS_LABEL[k]}`)
    .join(', ');
}
